import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebaseConfig';
import { collection, getDocs, doc, deleteDoc, writeBatch } from 'firebase/firestore';
import Layout from './Layout';
import './FavoriteTenders.css';

const FavoriteTenders = () => {
  const [gemFavorites, setGemFavorites] = useState([]);
  const [centralFavorites, setCentralFavorites] = useState([]);
  const [stateFavorites, setStateFavorites] = useState([]);

  // ---- Helpers ----
  const sanitizeBidNumber = (bidNumber) => bidNumber.replace(/\//g, '-');
  const sanitizeTenderID = (tenderID) => tenderID.replace(/\//g, '-');

  const formatDate = (value) => {
    if (!value) return 'N/A';

    // Firestore Timestamp
    if (value.seconds && value.nanoseconds !== undefined) {
      return new Date(value.seconds * 1000).toLocaleString();
    }

    // String or Date
    const d = new Date(value);
    if (isNaN(d)) return String(value);
    return d.toLocaleString();
  };

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const userId = auth.currentUser?.uid;
        if (!userId) return;

        // Fetch GeM Favorites
        const gemDocs = await getDocs(collection(db, 'users', userId, 'favoriteGeMTenders'));
        setGemFavorites(gemDocs.docs.map((doc) => doc.data()));

        // Fetch Central CPPP Favorites
        const centralDocs = await getDocs(collection(db, 'users', userId, 'favoriteCentralTenders'));
        setCentralFavorites(centralDocs.docs.map((doc) => doc.data()));

        // Fetch State CPPP Favorites
        const stateDocs = await getDocs(collection(db, 'users', userId, 'favoriteStateTenders'));
        setStateFavorites(stateDocs.docs.map((doc) => doc.data()));
      } catch (error) {
        console.error('Error fetching favorite tenders:', error);
      }
    };

    fetchFavorites();
  }, []);

  // --- Bulk Unmark Functions ---

  const removeAllGemFavorites = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId || gemFavorites.length === 0) return;

      const batch = writeBatch(db);
      gemFavorites.forEach((tender) => {
        const docId = sanitizeBidNumber(tender.bidNumber);
        batch.delete(doc(db, 'users', userId, 'favoriteGeMTenders', docId));
      });
      await batch.commit();
      setGemFavorites([]);
    } catch (error) {
      console.error('Error removing all GeM favorites:', error);
    }
  };

  const removeAllCentralFavorites = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId || centralFavorites.length === 0) return;

      const batch = writeBatch(db);
      centralFavorites.forEach((tender) => {
        const docId = sanitizeTenderID(tender.tenderID);
        batch.delete(doc(db, 'users', userId, 'favoriteCentralTenders', docId));
      });
      await batch.commit();
      setCentralFavorites([]);
    } catch (error) {
      console.error('Error removing all Central favorites:', error);
    }
  };

  const removeAllStateFavorites = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId || stateFavorites.length === 0) return;

      const batch = writeBatch(db);
      stateFavorites.forEach((tender) => {
        const docId = sanitizeTenderID(tender.tenderID);
        batch.delete(doc(db, 'users', userId, 'favoriteStateTenders', docId));
      });
      await batch.commit();
      setStateFavorites([]);
    } catch (error) {
      console.error('Error removing all State favorites:', error);
    }
  };

  // --- Per-item Unmark Functions ---

  const unmarkGemFavorite = async (bidNumber) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      const docId = sanitizeBidNumber(bidNumber);
      await deleteDoc(doc(db, 'users', userId, 'favoriteGeMTenders', docId));

      setGemFavorites((prev) => prev.filter((t) => t.bidNumber !== bidNumber));
    } catch (error) {
      console.error('Error unmarking single GeM favorite:', error);
    }
  };

  const unmarkCentralFavorite = async (tenderID) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      const docId = sanitizeTenderID(tenderID);
      await deleteDoc(doc(db, 'users', userId, 'favoriteCentralTenders', docId));

      setCentralFavorites((prev) => prev.filter((t) => t.tenderID !== tenderID));
    } catch (error) {
      console.error('Error unmarking single Central favorite:', error);
    }
  };

  const unmarkStateFavorite = async (tenderID) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      const docId = sanitizeTenderID(tenderID);
      await deleteDoc(doc(db, 'users', userId, 'favoriteStateTenders', docId));

      setStateFavorites((prev) => prev.filter((t) => t.tenderID !== tenderID));
    } catch (error) {
      console.error('Error unmarking single State favorite:', error);
    }
  };

  return (
    <Layout>
      <div className="favorites-container">
        <h1>Favorite Tenders</h1>

        {/* GeM Favorites Section */}
        <div className="favorites-section">
          <h2>GeM Favorites</h2>
          {gemFavorites.length ? (
            <>
              <button onClick={removeAllGemFavorites}>
                Unmark All GeM Favorites
              </button>
              <ul>
                {gemFavorites.map((tender, index) => (
                  <li key={index} className="tender-card">
                    <p><strong>Bid Number:</strong> {tender.bidNumber}</p>
                    <p><strong>Items:</strong> {tender.items}</p>
                    <p><strong>District:</strong> {tender.district}</p>
                    <p><strong>Bid Type:</strong> {tender.bidType}</p>
                    <p><strong>End Date:</strong> {formatDate(tender.endDate)}</p>
                    <p>
                      <strong>Bid Link:</strong>{' '}
                      {tender.bidURL ? (
                        <a
                          href={tender.bidURL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {tender.bidURL}
                        </a>
                      ) : (
                        'N/A'
                      )}
                    </p>
                    <button
                      className="unmark-single-button"
                      onClick={() => unmarkGemFavorite(tender.bidNumber)}
                    >
                      Unmark
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="no-favorites">No GeM favorites found.</p>
          )}
        </div>

        {/* Central CPPP Favorites Section */}
        <div className="favorites-section">
          <h2>Central CPPP Favorites</h2>
          {centralFavorites.length ? (
            <>
              <button onClick={removeAllCentralFavorites}>
                Unmark All Central Favorites
              </button>
              <ul>
                {centralFavorites.map((tender, index) => (
                  <li key={index} className="tender-card">
                    <p><strong>Tender ID:</strong> {tender.tenderID}</p>
                    <p><strong>Title:</strong> {tender.title}</p>
                    <p><strong>Organization:</strong> {tender.organization}</p>
                    <p>
                      <strong>Portal Link:</strong>{' '}
                      <a href={tender.portalLink} target="_blank" rel="noopener noreferrer">
                        {tender.portalLink}
                      </a>
                    </p>
                    <p><strong>End Date:</strong> {formatDate(tender.endDate)}</p>
                    <button
                      className="unmark-single-button"
                      onClick={() => unmarkCentralFavorite(tender.tenderID)}
                    >
                      Unmark
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="no-favorites">No Central CPPP favorites found.</p>
          )}
        </div>

        {/* State CPPP Favorites Section */}
        <div className="favorites-section">
          <h2>State CPPP Favorites</h2>
          {stateFavorites.length ? (
            <>
              <button onClick={removeAllStateFavorites}>
                Unmark All State Favorites
              </button>
              <ul>
                {stateFavorites.map((tender, index) => (
                  <li key={index} className="tender-card">
                    <p><strong>Tender ID:</strong> {tender.tenderID}</p>
                    <p><strong>Title:</strong> {tender.title}</p>
                    <p><strong>State:</strong> {tender.state}</p>
                    <p>
                      <strong>Portal Link:</strong>{' '}
                      <a href={tender.portalLink} target="_blank" rel="noopener noreferrer">
                        {tender.portalLink}
                      </a>
                    </p>
                    <p><strong>End Date:</strong> {formatDate(tender.endDate)}</p>
                    <button
                      className="unmark-single-button"
                      onClick={() => unmarkStateFavorite(tender.tenderID)}
                    >
                      Unmark
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="no-favorites">No State CPPP favorites found.</p>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default FavoriteTenders;
